import { DisplayWorkoutStatus } from '../../components/badge/workout-status-badge';
import {
  AthleteActualWorkoutLayer,
  AthleteDraftWorkoutLayer,
  AthletePublishedWorkoutLayer,
  BlockDraftWorkoutLayer,
  BlockPublishedWorkoutLayer,
} from './types';

export type WorkoutStatusById = {
  workoutId: string;
  status: DisplayWorkoutStatus;
};

export const getWorkoutStatusesForBase = (
  blockDraftLayers: BlockDraftWorkoutLayer[],
  blockPublishedLayers: BlockPublishedWorkoutLayer[],
  athletePublishedLayers: AthletePublishedWorkoutLayer[],
  actualWorkoutLayers: AthleteActualWorkoutLayer[],
  isSingleAthlete: boolean,
): WorkoutStatusById[] => {
  return blockDraftLayers.map((layer) => {
    const blockPublishedLayer = blockPublishedLayers.find(
      (published) =>
        published.workoutId === layer.workoutId && published.isCurrent,
    );
    if (!blockPublishedLayer) {
      return {
        workoutId: layer.workoutId,
        status: { type: 'draft' },
      };
    }

    if (blockPublishedLayer.status === 'canceled') {
      return {
        workoutId: layer.workoutId,
        status: { type: 'canceled' },
      };
    }

    const hasActual = !!actualWorkoutLayers.find(
      (actual) => actual.workoutId === layer.workoutId,
    );
    const athleteStatuses = athletePublishedLayers.flatMap((l) => {
      return l.workoutId === layer.workoutId ? l.status : [];
    });
    const hasUnpublishedChanges = layer.unpublishedChangeCount > 0;

    const workoutStatusForBase = getWorkoutStatusForBase(
      blockPublishedLayer.status,
      athleteStatuses,
      hasActual,
      hasUnpublishedChanges,
      isSingleAthlete,
    );

    return {
      workoutId: layer.workoutId,
      status: workoutStatusForBase,
    };
  });
};

export const getWorkoutStatusesForAthlete = (
  blockDraftLayers: BlockDraftWorkoutLayer[],
  athleteDraftLayers: AthleteDraftWorkoutLayer[],
  athletePublishedLayers: AthletePublishedWorkoutLayer[],
  actualWorkoutLayers: AthleteActualWorkoutLayer[],
  blockPublishedLayers: BlockPublishedWorkoutLayer[] = [],
): WorkoutStatusById[] => {
  // have to consider all possible draft workouts
  // since a workout could exist on the base block
  // but not yet be created on the athlete since the workout
  // hasnt yet been published/modified
  const allDraftWorkoutsIds = Array.from(
    new Set(
      [...blockDraftLayers, ...athleteDraftLayers].map((l) => l.workoutId),
    ),
  );
  return allDraftWorkoutsIds.map((draftWorkoutId) => {
    const athletePublishedLayer = athletePublishedLayers.find(
      (published) => published.workoutId === draftWorkoutId,
    );
    const athleteDraftLayer = athleteDraftLayers.find(
      (draft) => draft.workoutId === draftWorkoutId,
    );
    if (!athletePublishedLayer) {
      return {
        workoutId: draftWorkoutId,
        status: { type: 'draft' },
      };
    }
    const blockPublishedLayer = blockPublishedLayers.find(
      (published) =>
        published.workoutId === draftWorkoutId && published.isCurrent,
    );
    if (blockPublishedLayer?.status === 'canceled') {
      return {
        workoutId: draftWorkoutId,
        status: { type: 'canceled' },
      };
    }
    const hasActual = !!actualWorkoutLayers.find(
      (actual) => actual.workoutId === draftWorkoutId,
    );
    const hasUnpublishedChanges = athleteDraftLayer
      ? athleteDraftLayer?.unpublishedChangeCount > 0
      : false;
    return {
      workoutId: draftWorkoutId,
      status: getWorkoutStatusForAthlete(
        athletePublishedLayer.status,
        hasActual,
        hasUnpublishedChanges,
      ),
    };
  });
};

function getWorkoutStatusForBase(
  baseStatus: string,
  athleteStatuses: string[],
  hasActual: boolean,
  hasUnpublishedChanges: boolean,
  isSingleAthlete: boolean,
): DisplayWorkoutStatus {
  if (baseStatus === 'canceled') {
    return {
      type: baseStatus,
    };
  }

  // Todo: is this what we want when a coach marks the base workout as complete?
  if (baseStatus === 'completed') {
    return {
      type: baseStatus,
      completed: 1,
      toComplete: 1,
      readOnly: isSingleAthlete, // completed single athlete workouts are read only, otherwise false and editable
    };
  }

  // if workout has any completed athletes
  const completedAthleteWorkouts = athleteStatuses.filter(
    (status) => status === 'completed',
  );
  if (completedAthleteWorkouts.length > 0) {
    return {
      type: 'completed',
      completed: completedAthleteWorkouts.length,
      toComplete: athleteStatuses.length,
      readOnly: isSingleAthlete, // completed single athlete workouts are read only, otherwise false and editable
    };
  }

  // Single-athlete blocks share the base sheet with the sole athlete's terminal
  // status. Preserve skipped/canceled so the sheet stays read-only instead of
  // falling through to planned (or unpublished-changes / in-progress).
  if (isSingleAthlete) {
    const soleAthleteStatus = athleteStatuses[0];
    if (soleAthleteStatus === 'skipped' || soleAthleteStatus === 'canceled') {
      return { type: soleAthleteStatus };
    }
  }

  // if the workout has unpublished changes
  // note: we check this before in-progress because it has priority
  if (hasUnpublishedChanges) {
    return {
      type: 'unpublished-changes',
    };
  }

  // if any athlete logged actuals for this workout
  if (hasActual) {
    return {
      type: 'in-progress',
      readOnly: false,
    };
  }

  // only for the Block Sheet to show total # of athletes with actuals for workout
  return {
    type: 'planned',
  };
}

function getWorkoutStatusForAthlete(
  type: string,
  hasActual: boolean,
  hasUnpublishedChanges: boolean,
): DisplayWorkoutStatus {
  if (type === 'skipped' || type === 'canceled') {
    return {
      type,
    };
  }
  if (type === 'completed') {
    return {
      type,
      completed: 1,
      toComplete: 1,
      readOnly: true,
    };
  }
  if (hasActual) {
    return {
      type: 'in-progress',
      readOnly: false,
    };
  }

  if (hasUnpublishedChanges) {
    return {
      type: 'unpublished-changes',
    };
  }

  return {
    type: 'planned',
  };
}
